/// <reference types="vite-plugin-pwa/react" />
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { DURACAO, EASE_SAIDA, SO_OPACIDADE, useMovimentoReduzido } from '../lib/movimento';
import { Icone } from './Icone';
import { Botao } from './UI';

/*
 * Aviso de versão nova do Chama Um.
 *
 * O service worker novo fica esperando (registerType 'prompt', vite.config.ts)
 * até a pessoa escolher. "Atualizar agora" manda SKIP_WAITING e recarrega a
 * página quando o SW novo assume: tela, rotas e recursos novos de verdade, não
 * a casca velha em cache. "Depois" esconde o aviso por um tempo; ele volta, e
 * se a pessoa fechar todas as abas o SW novo assume sozinho na próxima visita.
 *
 * Não rouba o foco: é uma região `status`, anunciada sem interromper quem
 * está preenchendo um pedido no meio.
 */

const VERIFICAR_A_CADA_MS = 30 * 60 * 1000;
const ADIAR_MS = 60 * 60 * 1000;
// Se o SW novo não assumir nesse prazo, recarrega mesmo assim: nunca deixar
// a pessoa presa no botão girando.
const LIMITE_TROCA_MS = 4000;

export function AvisoAtualizacao() {
  const reduzido = useMovimentoReduzido();
  const [adiado, setAdiado] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const registroRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [temNova],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registro) {
      if (!registro) return;
      registroRef.current = registro;
      const verificar = () => {
        // Sem rede, a checagem falharia à toa: espera a próxima.
        if (!navigator.onLine || registro.installing) return;
        void registro.update().catch(() => {});
      };
      window.setInterval(verificar, VERIFICAR_A_CADA_MS);
      // Quem deixa o app aberto no celular e volta horas depois vê a versão
      // nova assim que a aba volta a ficar visível.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') verificar();
      });
    },
  });

  useEffect(() => {
    if (!adiado) return;
    const t = window.setTimeout(() => setAdiado(false), ADIAR_MS);
    return () => window.clearTimeout(t);
  }, [adiado]);

  async function atualizar() {
    setAtualizando(true);
    /*
     * Não depende só do updateServiceWorker(true): ele recarrega no evento
     * `controlling` do workbox-window, que não dispara quando o SW em espera
     * foi instalado numa visita anterior (SW "externo") — o botão ficava
     * girando para sempre. Aqui o reload vem do `controllerchange` do próprio
     * navegador, com prazo de segurança.
     */
    let recarregou = false;
    const recarregar = () => {
      if (recarregou) return;
      recarregou = true;
      window.location.reload();
    };
    navigator.serviceWorker?.addEventListener('controllerchange', recarregar, { once: true });
    window.setTimeout(recarregar, LIMITE_TROCA_MS);
    try {
      const esperando = registroRef.current?.waiting;
      if (esperando) esperando.postMessage({ type: 'SKIP_WAITING' });
      else await updateServiceWorker(true);
    } catch {
      recarregar();
    }
  }

  const visivel = temNova && !adiado;

  return (
    <div role="status" aria-live="polite" className="aviso-atualizacao-ancora">
      <AnimatePresence>
        {visivel && (
          <m.section
            key="aviso-atualizacao"
            aria-labelledby="t-aviso-atualizacao"
            className="aviso-atualizacao"
            initial={reduzido ? { opacity: 0 } : { opacity: 0, y: 16 }}
            animate={reduzido ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduzido ? { opacity: 0 } : { opacity: 0, y: 16 }}
            transition={reduzido ? SO_OPACIDADE : { duration: DURACAO.base, ease: EASE_SAIDA }}
          >
            <Icone nome="atualizar" className="aviso-atualizacao-icone" />
            <div className="aviso-atualizacao-texto">
              <p id="t-aviso-atualizacao" className="font-bold">Tem uma versão nova do Chama Um</p>
              <p>Atualize para usar as novidades. Leva só um instante.</p>
            </div>
            <div className="aviso-atualizacao-acoes">
              <Botao type="button" carregando={atualizando} onClick={() => void atualizar()}>
                Atualizar agora
              </Botao>
              <button type="button" className="acao-link" disabled={atualizando} onClick={() => setAdiado(true)}>
                Depois
              </button>
            </div>
          </m.section>
        )}
      </AnimatePresence>
    </div>
  );
}
